/**
 * Utilities for building PipelineSaveRequest from canvas state.
 * Handles converting ReactFlow nodes/edges into backend DTOs.
 */

import { Node, Edge } from '@xyflow/react';
import { PipelineSaveRequest, TransformDefinition, HopDefinition, PipelineMetadata } from '../../../../api/types';
import { createContextLogger } from '../../../../utils/logger';

const logger = createContextLogger('pipelineBuilder');

/**
 * Build PipelineSaveRequest from canvas nodes and edges
 */
export function buildPipelineSaveRequest(
  nodes: Node[],
  edges: Edge[],
  pipelineName: string,
  pipelineType: 'pipeline' | 'workflow',
  description?: string
): PipelineSaveRequest {
  // Filter out note nodes - they shouldn't be included in the pipeline structure
  const transformNodes = nodes.filter(node => node.type !== 'noteNode');
  
  logger.debug('Building pipeline save request:', {
    totalNodes: nodes.length,
    transformNodes: transformNodes.length,
    edges: edges.length,
    pipelineName,
    pipelineType
  });

  const metadata: PipelineMetadata = {
    name: pipelineName,
    description: description || '',
    type: pipelineType === 'workflow' ? 1 : 0,
    parameters: [],
  };

  const transforms: TransformDefinition[] = transformNodes.map((node) => {
    const transform = {
      name: node.data?.name || node.data?.label || node.id,
      originalName: node.data?._state?.originalValues?.name || undefined,
      type: node.data?.type || 'Unknown',
      xLocation: Math.round(node.position.x),
      yLocation: Math.round(node.position.y),
      description: node.data?.description || undefined,
    };
    
    logger.debug('Transform definition:', {
      nodeId: node.id,
      transform
    });
    
    return transform;
  });

  const hops: HopDefinition[] = edges.map((edge) => {
    const sourceNode = transformNodes.find((n) => n.id === edge.source);
    const targetNode = transformNodes.find((n) => n.id === edge.target);
    
    const hop = {
      from: sourceNode?.data?.name || sourceNode?.data?.label || edge.source,
      to: targetNode?.data?.name || targetNode?.data?.label || edge.target,
      enabled: edge.data?.enabled !== false,
      hopType: edge.data?.hopType || 0,
    };
    
    logger.debug('Hop definition:', { edgeId: edge.id, hop });
    
    return hop;
  });

  const request = {
    metadata,
    transforms,
    hops,
  };
  
  logger.info('Built pipeline save request:', {
    pipelineName,
    transformCount: transforms.length,
    hopCount: hops.length,
    request
  });

  return request;
}

/**
 * Extract pipeline name from file path
 */
export function extractPipelineNameFromPath(path: string): string {
  const filename = path.split('/').pop() || 'pipeline';
  return filename.replace(/\.(hpl|hwf)$/, '');
}

/**
 * Determine if path is a workflow or pipeline
 */
export function getFileTypeFromPath(path: string): 'pipeline' | 'workflow' {
  return path.endsWith('.hwf') ? 'workflow' : 'pipeline';
}

/**
 * Get file extension for type
 */
export function getFileExtension(type: 'pipeline' | 'workflow'): string {
  return type === 'workflow' ? '.hwf' : '.hpl';
}

